import { useDispatch, useSelector } from 'react-redux'
import Page from '../../common/Page'
import PageCard from '../../common/PageCard'
import Actions from '../../../actions'
import { routes } from '../../../consts'
import $t from '../../../i18n'
import { useEffect, useState } from 'react'
import { Button, Collapse, Switch } from 'antd'
import medical from '../../../resources/setting_medical.png'
import benefit_and_plan_information from '../../../resources/settings_benefit_and_plan_information.png'
import promotions_and_services from '../../../resources/settings_promotions_and_services.png'
import config from '../../../config.json'
import { t } from '@transifex/native'

import './CommunicationPreferences.css'

const CommunicationPreferences = () => {
  const dispatch = useDispatch()
  const { Panel } = Collapse
  const userDetails = useSelector(state => state.profile?.userDetails)
  const ownerId = useSelector(state => state.profile?.owner)
  const communicationPreferences = userDetails?.communicationPreferences
  const isCommunicationPreferencesConfigureMode = useSelector(
    state => state.profile?.isCommunicationPreferencesConfigureMode
  )
  const [configureMode, setConfigureMode] = useState(
    isCommunicationPreferencesConfigureMode
  )
  const isCommunicationPreferencesUpdateLoading = useSelector(
    state => state.profile?.isCommunicationPreferencesUpdateLoading
  )
  const updateCommunicationPreferencesError = useSelector(
    state => state.profile.updateCommunicationPreferencesError
  )
  const getCommunicationPreferencesByGroup = () => {
    let communicationByGroup = {}
    for (let item of communicationPreferences) {
      const group = item['group']
      if (communicationByGroup.hasOwnProperty(group)) {
        communicationByGroup[group].push(item)
      } else {
        communicationByGroup[group] = []
        communicationByGroup[group].push(item)
      }
    }
    return communicationByGroup
  }
  const communicationByGroup = getCommunicationPreferencesByGroup()
  const [preferences, setPreferences] = useState(communicationByGroup)

  // go over all the preferences and check if send_sms and send_email are null put them as true
  // the default value is true
  useEffect(() => {
    const newPreferences = {}
    for (let group in preferences) {
      newPreferences[group] = preferences[group].map(item => ({
        ...item,
        send_sms: item?.send_sms === null ? true : item?.send_sms,
        send_email: item?.send_email === null ? true : item?.send_email,
      }))
    }
    setPreferences(newPreferences)
  }, [])

  const changeSwitchSms = (category, group) => {
    const newPreferences = preferences[group].map(item =>
      item.category === category ? { ...item, send_sms: !item?.send_sms } : item
    )
    setPreferences({ ...preferences, [group]: newPreferences })
  }

  const changeSwitchEmail = (category, group) => {
    const newPreferences = preferences[group].map(item =>
      item?.category === category
        ? { ...item, send_email: !item?.send_email }
        : item
    )
    setPreferences({ ...preferences, [group]: newPreferences })
  }

  const getIconByGroup = group => {
    switch (group) {
      case 'Medical':
        return medical
      case 'Benefit and plan information':
        return benefit_and_plan_information
      case 'Promotions and services':
        return promotions_and_services
      default:
        return ''
    }
  }

  const onSave = () => {
    dispatch(
      Actions.createAction(Actions.UPDATE_COMMUNICATION_PREFERENCES_REQUESTED, {
        preferences: Object.values(preferences).flat(),
        isCommunicationPreferencesConfigureMode,
      })
    )
  }

  const getGroupHeader = (group, categories) => {
    return (
      <div className="group-header">
        <div className="group-title">
          <img src={getIconByGroup(group)} alt="" className="img" />
          {t(group)}
        </div>
        {!configureMode ? (
          <div className="group-details">
            {categories.map(category => category?.category).join(', ')}
          </div>
        ) : null}
      </div>
    )
  }

  const renderPageSubtitle = () => {
    return (
      <div className="page-subtitle">
        {configureMode
          ? $t('pages.communicationPreferences.subtitleConfigureMode')
          : $t('pages.communicationPreferences.subtitle')}
        <a
          href={config.termsURL}
          rel="noreferrer"
          target="_blank"
          className="communicationPreferences-tandc trackable link"
          data-name="terms-and-conditions"
        >
          {$t('pages.communicationPreferences.termsOfUse')}{' '}
        </a>
        {$t('pages.communicationPreferences.and')}{' '}
        <a
          href={config.privacyURL}
          target="_blank"
          rel="noreferrer"
          data-name="privacy-policy"
          className="communicationPreferences-privacy-policy trackable link"
        >
          {$t('pages.communicationPreferences.privacyPolicy')}{' '}
        </a>
      </div>
    )
  }

  const renderGroupReferences = (group, categories) => {
    return (
      <Panel
        key={group}
        header={getGroupHeader(group, categories)}
        className="panel"
      >
        {categories.map((category, index) => {
          return (
            <div className="preferences-container">
              <div className="category-title">{t(category?.category)}</div>
              <div className="category-details">
                {$t(
                  `pages.communicationPreferences.categoriesDetails.${category?.category
                    .replaceAll(',', '')
                    .split(' ')
                    .join('_')
                    .toLowerCase()}`
                )}
              </div>
              <div className="switch-container">
                <span>{$t('pages.communicationPreferences.sms')}</span>
                <Switch
                  onChange={() => changeSwitchSms(category?.category, group)}
                  className="switch"
                  // if the send_sms come back from the be as null then the default value is true so the switch will be on
                  checked={
                    category?.send_sms === null ? true : !!category?.send_sms
                  }
                ></Switch>
                {category?.group === 'Promotions and services' ? (
                  <>
                    <span>{$t('pages.communicationPreferences.email')}</span>
                    <Switch
                      onChange={() =>
                        changeSwitchEmail(category?.category, group)
                      }
                      className="switch"
                      // if the send_email come back from the be as null then the default value is true so the switch will be on
                      checked={
                        category?.send_email === null
                          ? true
                          : !!category?.send_email
                      }
                    ></Switch>
                  </>
                ) : null}
              </div>
              {index !== categories.length - 1 ? (
                <div className="divider"></div>
              ) : null}
            </div>
          )
        })}
      </Panel>
    )
  }

  const pageActions = configureMode
    ? [
        <Button
          key="1"
          className="button-container trackable"
          type="primary"
          data-name="save"
          onClick={onSave}
        >
          {$t('common.save')}
        </Button>,
      ]
    : [
        <Button
          key="1"
          className="button-container trackable"
          type="primary"
          onClick={onSave}
          data-name="accept"
        >
          {$t('common.accept')}
        </Button>,
        <Button
          key="2"
          data-name="configure"
          className="button-container trackable"
          type="secondary"
          onClick={() => setConfigureMode(true)}
        >
          {$t('common.configure')}
        </Button>,
      ]

  return (
    <PageCard
      className={
        isCommunicationPreferencesConfigureMode
          ? 'communication-preferences-page'
          : 'communication-preferences-page login-page-card'
      }
      hideAlertBanner={!isCommunicationPreferencesConfigureMode}
      enableBack={isCommunicationPreferencesConfigureMode}
      onBack={() =>
        dispatch(
          Actions.createAction(
            Actions.NAVIGATED_TO,
            routes.PROFILE_ROUTE.SETTING_PREFERENCES
          )
        )
      }
    >
      <Page
        title={$t('pages.communicationPreferences.title')}
        subtitle={renderPageSubtitle()}
        loading={isCommunicationPreferencesUpdateLoading}
        hideAlertBanner
      >
        <div className="scroll-view">
          <Collapse
            collapsible={!configureMode ? 'disabled' : 'header'}
            expandIconPosition="end"
            expandIcon={({ isActive }) =>
              configureMode ? (
                <span
                  className={
                    isActive ? 'icon-arrow-bold-up' : 'icon-arrow-bold-down'
                  }
                />
              ) : null
            }
            className="collapse"
          >
            {Object.keys(preferences)?.map((group, i) =>
              renderGroupReferences(group, preferences[group])
            )}
          </Collapse>
          <div>
            {$t('pages.communicationPreferences.subtitleContinue')}
            <br />
            {userDetails?.id === ownerId
              ? $t('pages.communicationPreferences.disclaimerOwner')
              : ''}
          </div>
        </div>
        {updateCommunicationPreferencesError && (
          <div className="error">{updateCommunicationPreferencesError}</div>
        )}
        <div className="page-footer">{pageActions}</div>
      </Page>
    </PageCard>
  )
}
export default CommunicationPreferences
